import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const baseButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm md:text-base font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-safety focus-visible:ring-offset-2 focus-visible:ring-offset-navy";

const primaryButtonClass =
  `${baseButtonClass} bg-safety text-white hover:bg-[#e66e00]`;

const secondaryButtonClass =
  `${baseButtonClass} border border-white/30 text-white hover:bg-white/5`;

function CTAButton({ item, variant = "primary" }) {
  if (!item?.label || !item?.to) return null;

  const className = variant === "primary" ? primaryButtonClass : secondaryButtonClass;
  const isExternal =
    item.to.startsWith("http://") ||
    item.to.startsWith("https://") ||
    item.to.startsWith("mailto:");

  const content = (
    <>
      <span>{item.label}</span>
      {variant === "primary" && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
    </>
  );

  if (isExternal) {
    return (
      <a
        href={item.to}
        target={item.to.startsWith("mailto:") ? undefined : "_blank"}
        rel={item.to.startsWith("mailto:") ? undefined : "noopener noreferrer"}
        data-testid={item.testId}
        className={className}
        aria-label={item.ariaLabel || item.label}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={item.to}
      data-testid={item.testId}
      className={className}
      aria-label={item.ariaLabel || item.label}
    >
      {content}
    </Link>
  );
}

export default function CTASection({
  id,
  eyebrow = "Get in touch",
  title = "Talk to us about railway training and development.",
  intro,
  primary = {
    label: "Contact us",
    to: "/contact",
    testId: "cta-contact",
  },
  secondary,
}) {
  return (
    <section
      id={id}
      className="relative overflow-hidden bg-navy text-white"
      data-testid="cta-section"
      aria-labelledby={id ? `${id}-title` : undefined}
    >
      <div className="absolute inset-0 grid-bg opacity-[0.06]" aria-hidden="true" />

      <div className="container-rail py-20 md:py-24 relative">
        <div className="max-w-3xl">
          {eyebrow && (
            <p className="label-eyebrow text-safety">
              {eyebrow}
            </p>
          )}

          <h2
            id={id ? `${id}-title` : undefined}
            className="mt-4 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-white text-balance leading-[1.1]"
          >
            {title}
          </h2>

          {intro && (
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
              {intro}
            </p>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton item={primary} variant="primary" />
            <CTAButton item={secondary} variant="secondary" />
          </div>
        </div>
      </div>
    </section>
  );
}